/**
 * ResolutionBanner — teal gradient strip prompting signature.
 * Ported from app.jsx (ResolutionBanner) in the Claude Design output.
 *
 * Phase 11: driven by the live `GET /v1/resolutions?awaitingMySignature=true`
 * query via `useResolutions`. Renders nothing when the caller has no
 * awaiting resolutions (no fixture fallback — banner is conditional UI,
 * not a permanent dashboard fixture).
 *
 * The link params come from the real resolution + trust ids returned by
 * the backend. Previously the link hardcoded
 *   { id: 'vandermerwe-family-trust', rid: 'RES-2026-Q3-04' }
 * which sent every user to Anna's fixture detail page regardless of who
 * they were — a demo-clickthrough trap.
 */

import { Link } from '@tanstack/react-router';
import { Icon } from '../icon';
import { ProgressRing } from '../charts/progress-ring';
import { fmtR } from '../../lib/fmt-r';
import { useResolutions, type ResolutionSummary } from '../../lib/api';

export function ResolutionBanner() {
  const { data, isLoading } = useResolutions({
    awaitingMySignature: true,
    limit: 1,
  });

  // Render nothing while the query is pending or if the caller has no
  // resolutions awaiting their signature. The dashboard layout collapses
  // gracefully — this is conditional UI, not a permanent slot.
  if (isLoading) return null;
  const first = data?.resolutions[0];
  if (!first) return null;

  return <Banner resolution={first} />;
}

function Banner({ resolution }: { resolution: ResolutionSummary }) {
  const r = resolution;
  const pct =
    r.signers_total > 0 ? (r.signers_signed / r.signers_total) * 100 : 0;

  // Avatars for the unsigned slots — we don't yet have signer display info
  // on the list response, so render anonymous slots for each unsigned seat
  // and one filled slot for each already-signed seat. The detail screen
  // surfaces the real names once the user taps through.
  const unsignedCount = Math.max(r.signers_total - r.signers_signed, 0);
  const signedCount = r.signers_signed;

  return (
    <section className="resolution-banner">
      <div>
        <div className="eyebrow on-dark">Awaiting your signature</div>
        {/* 2026-05-20 (Batch 8 / F02-P-072): the trailing period was hard-
            coded in JSX, but resolution titles often already end with a
            qualifier like "— Q3 2026", which made the banner read with a
            stray "...— Q3 2026." typo. Render the title as-is; the
            heading is a noun-phrase, not a sentence. */}
        <h3 className="res-title">{r.title}</h3>
        <div className="res-meta">
          <span>Resolution {r.ref}</span>
          {r.amount !== undefined && (
            <>
              <span className="dot" />
              <span>R&nbsp;{fmtR(r.amount)}</span>
            </>
          )}
          {r.deadline && (
            <>
              <span className="dot" />
              <span>Effective {r.deadline}</span>
            </>
          )}
          <span className="dot" />
          <span style={{ color: 'var(--accent-300)' }}>
            {r.signers_signed} of {r.signers_total} trustees signed
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 18 }}>
          <div className="signers">
            {Array.from({ length: signedCount }).map((_, i) => (
              <div key={`s-${i}`} className="avatar" aria-hidden="true">
                {' '}
              </div>
            ))}
            {Array.from({ length: unsignedCount }).map((_, i) => (
              <div
                key={`u-${i}`}
                className="avatar unsigned"
                aria-hidden="true"
              >
                {' '}
              </div>
            ))}
          </div>
          <div style={{ position: 'relative', width: 56, height: 56, marginLeft: 8, flexShrink: 0 }}>
            <ProgressRing pct={pct} size={56} stroke={3} />
            <div
              style={{
                position: 'absolute', inset: 0, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--mono)', fontSize: 12,
                color: 'var(--accent-200)', fontWeight: 500,
                fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                letterSpacing: '-0.02em',
              }}
            >
              {r.signers_signed}/{r.signers_total}
            </div>
          </div>
        </div>
      </div>
      <Link
        to="/app/trusts/$id/resolutions/$rid/sign"
        params={{ id: r.trustId, rid: r.id }}
        className="res-sign-btn"
        style={{ textDecoration: 'none' }}
      >
        <Icon name="sign" size={16} stroke={2} color="var(--primary-900)" />
        Review &amp; sign
      </Link>
    </section>
  );
}
